import React, { useEffect, useState } from "react";

import { PageNavigator } from "@components/page-navigator";
import { useUser } from "@components/user-context";
import { useNorthbeamQuery } from "@hooks/http";
import { type User } from "@internal/user";
import {
  ConnectionType,
  type FetchSanitizedConnectionsByType,
  type FetchSanitizedConnectionsByTypeVariables,
  type FetchSanitizedConnectionsByType_me_sanitizedConnections,
} from "@north-beam/nb-client-side";

import { FETCH_SANITIZED_CONNECTIONS_BY_TYPE } from "./queries";

export enum SettingsPath {
  PROFILE = "profile",
  BILLING = "billing",
  ACCOUNT_MANAGERS = "account-managers",
  EMAIL_NOTIFICATIONS = "email-notifications",
  CURRENCY = "currency",
  MANAGE_EXPORTS = "manage-exports",
  DATA_SPREADSHEETS = "data-spreadsheets",
  EMAIL_EXCLUSIONS = "email-exclusions",
  SUBSCRIPTION_ORDER_TAGS = "subscription-order-tags",
  EXCLUDE_ORDERS = "excluded-orders",
  META_SHOPS_ORDER_TAGS = "meta-shops-order-tags",
  TIK_TOK_SHOPS_ORDER_TAGS = "tik-tok-shops-order-tags",
  API_KEYS = "api-keys",
  CUSTOM_ANNOTATIONS = "custom-annotations",
  NORTHBEAM_LABS = "northbeam-labs",
  INTEGRATE_ENQUIRE_LABS = "enquire-labs-integration",
  DISCOUNT_CODE_MAPPINGS = "discount-code-mappings",
  CONNECTIONS = "connections",
  UTM_GUIDE = "utm-guide",
  TRACKING_SCRIPT_TAGS = "tracking-script-tags",
  TRACKING_EVENTS = "tracking-events",
  GA_PARAM_EXCLUSIONS = "google-analytics-parameters-exclusions",
  DNS_ENTRY = "dns-entry",
  ADDITIONAL_MANAGED_DOMAINS = "additional-managed-domains",
  USER_SETTINGS = "user-settings",
  MODEL_SETTINGS = "model-settings",
  APEX = "apex",
}

type SettingsRouteParams = {
  // TODO: We should look into trying to more strongly type the feature flags on a user.
  readonly user: User;
  readonly connection: FetchSanitizedConnectionsByType_me_sanitizedConnections | null;
};

interface _SettingsRoute<R extends SettingsPath = SettingsPath> {
  readonly label: string;
  readonly route: R;
  readonly adminOnly?: boolean;
  /* TODO: It would probably be more beneficial down the line to incorporate dynamic visibility into
     the type that is defined for the prop on the PageNavigator component, so the logic can be used
     outside of this page as well. */
  readonly visible?: boolean | ((params: SettingsRouteParams) => boolean);
}

type SettingsRoute<R extends SettingsPath = SettingsPath> =
  | _SettingsRoute<R>
  | ((params: SettingsRouteParams) => _SettingsRoute<R>);

type SettingsRouteGroup = {
  readonly title: string;
  readonly routes: SettingsRoute[];
};

const ROUTE_GROUPS: SettingsRouteGroup[] = [
  {
    title: "Account settings",
    routes: [
      { label: "Profile", route: SettingsPath.PROFILE },
      { label: "Billing", route: SettingsPath.BILLING },
      { label: "Account managers", route: SettingsPath.ACCOUNT_MANAGERS },
      { label: "Email notifications", route: SettingsPath.EMAIL_NOTIFICATIONS },
      {
        label: "Manage exports",
        route: SettingsPath.MANAGE_EXPORTS,
        visible: ({ user }) => user.featureFlags.enableDataExportManagementPage,
      },
      { label: "Currency", route: SettingsPath.CURRENCY },
      ({ user }) => ({
        label: "Northbeam Labs",
        route: SettingsPath.NORTHBEAM_LABS,
        adminOnly: user.isAdmin,
        visible: user.isAdmin,
      }),
      {
        label: "User settings",
        route: SettingsPath.USER_SETTINGS,
        visible: ({ user }) => user.featureFlags.userSettingsMenuItem,
      },
      {
        label: "Apex",
        route: SettingsPath.APEX,
        visible: ({ user }) => user.featureFlags.enableApexPage,
      },
      {
        label: "Model settings",
        route: SettingsPath.MODEL_SETTINGS,
        visible: ({ user }) => user.featureFlags.showProcessingSettings,
      },
    ],
  },
  {
    title: "Data mapping",
    routes: [
      {
        label: "Data Spreadsheets",
        route: SettingsPath.DATA_SPREADSHEETS,
      },
      {
        label: "Subscription order tags",
        route: SettingsPath.SUBSCRIPTION_ORDER_TAGS,
        visible: ({ user }) => user.featureFlags.showSubscriptionOrderTags,
      },
      {
        label: "Meta shop order tags",
        route: SettingsPath.META_SHOPS_ORDER_TAGS,
        visible: ({ user }) => user.featureFlags.enableShopInclusionByOrderTag,
      },
      {
        label: "TikTok shop order tags",
        route: SettingsPath.TIK_TOK_SHOPS_ORDER_TAGS,
        visible: ({ user }) => user.featureFlags.enableShopInclusionByOrderTag,
      },
      { label: "Exclude orders by tag", route: SettingsPath.EXCLUDE_ORDERS },
      {
        label: "Exclude emails from attribution matching",
        route: SettingsPath.EMAIL_EXCLUSIONS,
      },
      { label: "API Keys", route: SettingsPath.API_KEYS },
      { label: "Custom Annotations", route: SettingsPath.CUSTOM_ANNOTATIONS },
      {
        label: "Integrate Enquire Labs",
        route: SettingsPath.INTEGRATE_ENQUIRE_LABS,
        visible: ({ user, connection }) =>
          user.featureFlags.enquireLabsIntegration &&
          connection &&
          connection.connectionValidation,
      },
      {
        label: "Discount code mappings",
        route: SettingsPath.DISCOUNT_CODE_MAPPINGS,
        visible: ({ user }) => user.featureFlags.showDiscountCodeMappings,
      },
    ],
  },
  {
    title: "Onboarding tasks",
    routes: [
      { label: "Connections", route: SettingsPath.CONNECTIONS },
      { label: "UTM guide", route: SettingsPath.UTM_GUIDE },
      {
        label: "Northbeam Pixel and Event Tracking",
        route: SettingsPath.TRACKING_SCRIPT_TAGS,
      },
      {
        label: "Data Diagnostics",
        route: SettingsPath.TRACKING_EVENTS,
        visible: ({ user }) => user.featureFlags.pixelDiagnosticsPage,
      },
      {
        label: "Google Analytics parameter exclusions",
        route: SettingsPath.GA_PARAM_EXCLUSIONS,
      },
      { label: "Setup DNS Entry", route: SettingsPath.DNS_ENTRY },
      {
        label: "Additional managed domains",
        route: SettingsPath.ADDITIONAL_MANAGED_DOMAINS,
      },
    ],
  },
];

// TODO: Incorporate into PageNavigator component mechanics eventually...
const routeIsVisible = <R extends SettingsPath>(
  route: _SettingsRoute<R>,
  params: SettingsRouteParams,
): boolean => {
  if (route.visible !== undefined) {
    return typeof route.visible === "function"
      ? route.visible(params)
      : route.visible;
  }
  return true;
};

export const SettingsNav = () => {
  const [connection, setConnection] =
    useState<FetchSanitizedConnectionsByType_me_sanitizedConnections | null>(
      null,
    );
  const { data } = useNorthbeamQuery<
    FetchSanitizedConnectionsByType,
    FetchSanitizedConnectionsByTypeVariables
  >(FETCH_SANITIZED_CONNECTIONS_BY_TYPE, {
    variables: { type: ConnectionType.integration_shopify },
  });
  const { user } = useUser();

  useEffect(() => {
    setConnection(data?.me.sanitizedConnections[0] ?? null);
  }, [data?.me.sanitizedConnections]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      {ROUTE_GROUPS.map(({ routes: _routes, title }, i) => {
        const routes = _routes
          .map((r) => (typeof r === "function" ? r({ user, connection }) : r))
          .filter((r) => routeIsVisible(r, { user, connection }));
        return (
          <PageNavigator orderedLabelRoutes={routes} title={title} key={i} />
        );
      })}
    </div>
  );
};
